import { moneyFormat } from "../../../helpers/appHelpers";
import {
	AdjustmentType,
	SalaryAdjustmentDto,
	SalaryAdjustmentStatus,
	parseNotificationRecipients,
} from "../../../models/salary-adjustment.dto";

export const SALARY_ADJUSTMENT_STATUS_DISPLAY: Record<
	SalaryAdjustmentStatus,
	{ label: string; colors: string; dotColor: string }
> = {
	[SalaryAdjustmentStatus.PENDING]: {
		label: "Pending",
		colors: "bg-amber-50 text-amber-700",
		dotColor: "bg-amber-500",
	},
	[SalaryAdjustmentStatus.PROCESSING]: {
		label: "Processing",
		colors: "bg-blue-100 text-blue-600",
		dotColor: "bg-blue-500",
	},
	[SalaryAdjustmentStatus.PPROCESSED_SUCCESSFULLY]: {
		label: "Applied",
		colors: "bg-green-100 text-green-700",
		dotColor: "bg-green-500",
	},
	[SalaryAdjustmentStatus.FAILED_PROCESSING]: {
		label: "Failed",
		colors: "bg-red-100 text-red-600",
		dotColor: "bg-red-500",
	},
	[SalaryAdjustmentStatus.REVERSED]: {
		label: "Reversed",
		colors: "bg-gray-100 text-gray-500",
		dotColor: "bg-gray-400",
	},
	[SalaryAdjustmentStatus.EXPIRED]: {
		label: "Expired",
		colors: "bg-yellow-100 text-yellow-700",
		dotColor: "bg-yellow-500",
	},
};

export const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
	[AdjustmentType.INCREASE]: "Increase",
	[AdjustmentType.DECREASE]: "Decrease",
};

export const SalaryAdjustmentStatusBadge = ({
	status,
}: {
	status: SalaryAdjustmentStatus;
}) => {
	const display = SALARY_ADJUSTMENT_STATUS_DISPLAY[status] || {
		label: status,
		colors: "bg-gray-100 text-gray-500",
		dotColor: "bg-gray-400",
	};

	return (
		<span
			className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${display.colors}`}
		>
			<span className={`w-1.5 h-1.5 rounded-full ${display.dotColor}`} />
			{display.label}
		</span>
	);
};

/** Formats the adjustment value as a money amount or a percentage */
export const formatAdjustmentValue = (adjustment: SalaryAdjustmentDto) => {
	if (adjustment.adjustmentAmount !== null) {
		return moneyFormat(adjustment.adjustmentAmount);
	}
	if (adjustment.adjustmentPercent !== null) {
		// Round to avoid floating point noise, e.g. 0.07 * 100
		const percent = Number(
			(Number(adjustment.adjustmentPercent) * 100).toFixed(4),
		);
		return `${percent}%`;
	}
	return "--";
};

/**
 * What happens next to an adjustment that hasn't been applied yet. Ones with
 * a pay period are picked up by a job that runs every few minutes, so they
 * are applied shortly after their start date begins; ones without are
 * applied in the background right after they are saved.
 */
export const describeUpcomingProcessing = (
	adjustment: SalaryAdjustmentDto,
	payPeriodCodes?: Record<number, string>,
): string | null => {
	if (adjustment.status === SalaryAdjustmentStatus.PROCESSING) {
		return "Being applied now. It can't be edited or deleted while it is being applied.";
	}
	if (adjustment.status !== SalaryAdjustmentStatus.PENDING) return null;
	if (!adjustment.payPeriodId) {
		return "Waiting to be applied in the background. This usually takes a few moments; if processing is interrupted, it is retried automatically within several minutes.";
	}
	const payPeriod = formatAdjustmentPayPeriod(adjustment, payPeriodCodes);
	const notified = parseNotificationRecipients(
		adjustment.notificationRecipients,
	).length
		? " Notification recipients will be told the outcome."
		: "";
	return `Scheduled. It will be applied automatically shortly after the start date of ${payPeriod} begins, and can be edited or deleted until then.${notified}`;
};

/**
 * Whether a pay period has started (or has no start date), so an adjustment
 * can no longer be scheduled for it. Compares calendar days, as the server
 * stores start dates as dates and only accepts ones from tomorrow on.
 */
export const hasPayPeriodStarted = (
	startDate?: string | null,
	today: Date = new Date(),
): boolean => {
	if (!startDate) return true;
	const pad = (value: number) => String(value).padStart(2, "0");
	const todayDay = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(
		today.getDate(),
	)}`;
	return startDate.slice(0, 10) <= todayDay;
};

/** Pay period code when scheduled, otherwise the adjustment applies immediately */
export const formatAdjustmentPayPeriod = (
	adjustment: SalaryAdjustmentDto,
	payPeriodCodes?: Record<number, string>,
) => {
	if (!adjustment.payPeriodId) return "Immediately";
	return (
		payPeriodCodes?.[adjustment.payPeriodId] ||
		`Pay period #${adjustment.payPeriodId}`
	);
};
